import { useEffect, useState } from "react";
import { Plus, Trash2, GripVertical } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

const TAGS = ["", "NEW", "SALE", "TRENDING", "BESTSELLER", "EXCLUSIVE"];
const clone = (o) => JSON.parse(JSON.stringify(o));

function findItem(items, id) {
  for (const item of items) {
    if (item.id === id) return item;
    const found = findItem(item.children || [], id);
    if (found) return found;
  }
  return null;
}

function removeItem(items, id) {
  return items.filter((i) => i.id !== id).map((i) => ({ ...i, children: removeItem(i.children || [], id) }));
}

function ItemEditor({ item, onSave, onClose }) {
  const [f, setF] = useState(item);
  const setMega = (k, v) => setF({ ...f, mega: { ...(f.mega || {}), [k]: v } });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="bg-[#F7F2E7] border border-[#D4AF37]/50 p-4 space-y-3 mb-2" data-testid="menu-item-editor">
      <div className="grid sm:grid-cols-3 gap-3">
        <input required placeholder="Label" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid="menu-item-label" className="tq-input !py-2" />
        <input placeholder="URL (/collections/heels)" value={f.url || ""} onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="menu-item-url" className="tq-input !py-2" />
        <select value={f.tag || ""} onChange={(e) => setF({ ...f, tag: e.target.value })} data-testid="menu-item-tag" className="tq-input !py-2 bg-white">
          {TAGS.map((t) => <option key={t} value={t}>{t || "No label tag"}</option>)}
        </select>
      </div>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={f.enabled !== false} onChange={(e) => setF({ ...f, enabled: e.target.checked })} data-testid="menu-item-enabled" className="accent-[#D4AF37]" />
        Visible on storefront (enabled)
      </label>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={!!f.mega} onChange={(e) => setF({ ...f, mega: e.target.checked ? { heading: "", subheading: "", image: "", promo_text: "", promo_link: "" } : undefined })} className="accent-[#D4AF37]" />
        Mega menu panel (heading, image promo, columns)
      </label>
      {f.mega && (
        <div className="grid sm:grid-cols-2 gap-3 pl-5 border-l-2 border-[#D4AF37]">
          <input placeholder="Mega heading" value={f.mega.heading || ""} onChange={(e) => setMega("heading", e.target.value)} className="tq-input !py-2" />
          <input placeholder="Mega subheading" value={f.mega.subheading || ""} onChange={(e) => setMega("subheading", e.target.value)} className="tq-input !py-2" />
          <input placeholder="Promo image URL" value={f.mega.image || ""} onChange={(e) => setMega("image", e.target.value)} className="tq-input !py-2" />
          <input placeholder="Promo text" value={f.mega.promo_text || ""} onChange={(e) => setMega("promo_text", e.target.value)} className="tq-input !py-2" />
          <input placeholder="Promo link" value={f.mega.promo_link || ""} onChange={(e) => setMega("promo_link", e.target.value)} className="tq-input !py-2" />
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.new_tab || false} onChange={(e) => setF({ ...f, new_tab: e.target.checked })} className="accent-[#D4AF37]" /> Open in new tab</label>
        </div>
      )}
      <div className="flex gap-2">
        <button type="submit" data-testid="menu-item-save" className="tq-btn-dark !py-2 !px-4 !text-[10px]">Save Item</button>
        <button type="button" onClick={onClose} className="tq-btn-outline !py-2 !px-4 !text-[10px]">Cancel</button>
      </div>
    </form>
  );
}

export default function AdminMenus() {
  const [menus, setMenus] = useState(null);
  const [active, setActive] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [addingTo, setAddingTo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/menus").then(({ data }) => {
    setMenus(data);
    setActive((prev) => {
      const next = data.find((m) => m.id === (prev?.id || data[0]?.id));
      return next ? clone(next) : null;
    });
  });
  useEffect(() => { load(); }, []);

  const mutate = (fn) => { const next = clone(active); fn(next.items); setActive(next); };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.put(`/admin/menus/${active.id}`, active);
      toast.success("Menu saved — live on the storefront");
      load();
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setBusy(false); }
  };

  const move = (items, idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    [items[idx], items[j]] = [items[j], items[idx]];
  };

  const renderItems = (items, path, depth) => (
    <ul className={depth > 0 ? "ml-5 border-l border-[#E5DEC9] pl-3" : ""}>
      {items.map((item, i) => (
        <li key={item.id}>
          <div className="flex items-center gap-2 py-2 bg-white border border-[#E5DEC9] px-3 mb-2" data-testid={`menu-item-${(item.label || "item").toLowerCase().replace(/\s+/g, "-")}`}>
            <GripVertical size={14} className="text-[#c9c2b5] shrink-0" />
            <span className="text-sm font-medium flex-1 min-w-0 truncate">{item.label}
              {item.tag && <span className="ml-2 text-[8px] font-bold text-[#D4AF37] border border-[#D4AF37] px-1">{item.tag}</span>}
              {item.mega && <span className="ml-2 text-[8px] font-bold bg-[#121212] text-white px-1">MEGA</span>}
              <span className="block text-[10px] text-[#9a938a] font-normal truncate">{item.url}</span>
            </span>
            <button onClick={() => mutate((all) => { let list = all; for (const pid of path) list = findItem(all, pid).children; move(list, i, -1); })} aria-label="Move up" className="text-xs text-[#8C7A5E] px-1">↑</button>
            <button onClick={() => mutate((all) => { let list = all; for (const pid of path) list = findItem(all, pid).children; move(list, i, 1); })} aria-label="Move down" className="text-xs text-[#8C7A5E] px-1">↓</button>
            {depth < 2 && <button onClick={() => setAddingTo(item.id)} data-testid={`menu-add-sub-${i}`} className="text-xs text-[#C5A059] font-semibold whitespace-nowrap">+ Sub</button>}
            <button onClick={() => setEditingId(item.id)} data-testid={`menu-edit-${i}`} className="text-xs text-[#8C7A5E] font-semibold">Edit</button>
            <button onClick={() => mutate((all) => { const filtered = removeItem(all, item.id); all.splice(0, all.length, ...filtered); })} aria-label="Delete item" className="text-[#8B1E21]"><Trash2 size={13} /></button>
          </div>
          {editingId === item.id && (
            <ItemEditor item={clone(item)} onClose={() => setEditingId(null)}
              onSave={(f) => { mutate((all) => { const target = findItem(all, item.id); Object.keys(target).forEach((k) => delete target[k]); Object.assign(target, f); }); setEditingId(null); }} />
          )}
          {addingTo === item.id && (
            <ItemEditor item={{ id: crypto.randomUUID(), label: "", url: "", tag: "", enabled: true, children: [] }} onClose={() => setAddingTo(null)}
              onSave={(f) => { mutate((all) => { const target = findItem(all, item.id); target.children = [...(target.children || []), f]; }); setAddingTo(null); }} />
          )}
          {item.children?.length > 0 && renderItems(item.children, [...path, item.id], depth + 1)}
        </li>
      ))}
    </ul>
  );

  if (!menus) return <div className="h-48 bg-white animate-pulse" />;
  return (
    <div data-testid="admin-menus">
      <h1 className="font-serif text-3xl mb-2">Navigation & Mega Menus</h1>
      <p className="text-sm text-[#6E6D6A] mb-6">Edit labels, URLs, nesting (up to 3 levels) and mega menu panels. Save to publish instantly.</p>
      {menus.length > 1 && (
        <div className="flex gap-2 mb-4">
          {menus.map((m) => (
            <button key={m.id} onClick={() => setActive(clone(m))} data-testid={`menu-tab-${m.handle}`}
              className={`px-3 py-1.5 text-xs border ${active?.id === m.id ? "bg-[#121212] text-white border-[#121212]" : "border-[#E5DEC9] bg-white"}`}>{m.name}</button>
          ))}
        </div>
      )}
      {active && (
        <div className="bg-white border border-[#E5DEC9] p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <input value={active.name} onChange={(e) => setActive({ ...active, name: e.target.value })} data-testid="menu-name" className="tq-input !py-2 max-w-xs font-serif text-lg" />
            <button onClick={save} disabled={busy} data-testid="menu-save-btn" className="tq-btn-dark !py-2.5">{busy ? "Saving…" : "Save Menu"}</button>
          </div>
          {renderItems(active.items, [], 0)}
          <button onClick={() => setAddingTo("root")} data-testid="menu-add-item" className="tq-btn-outline !py-2 !px-4 mt-2"><Plus size={13} /> Add Menu Item</button>
          {addingTo === "root" && (
            <ItemEditor item={{ id: crypto.randomUUID(), label: "", url: "", tag: "", enabled: true, children: [] }}
              onClose={() => setAddingTo(null)}
              onSave={(f) => { setActive({ ...active, items: [...active.items, f] }); setAddingTo(null); }} />
          )}
        </div>
      )}
    </div>
  );
}
